import type { ReactNode } from 'react';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

interface BadgeProps {
  variant?: BadgeVariant;
  children: ReactNode;
  className?: string;
}

// Status colours use the Tailwind palette (not design tokens), so each needs an
// explicit dark variant — otherwise the light *-100 background stays a bright
// island on the dark canvas. Dark variants: deep translucent bg + light text.
const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-surface text-slate',
  success: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
  warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-200',
  danger: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  info: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
};

export function Badge({ variant = 'default', children, className = '' }: BadgeProps) {
  return (
    <span
      className={[
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        variantClasses[variant],
        className,
      ].join(' ')}
    >
      {children}
    </span>
  );
}

const STATUS_VARIANT_MAP: Record<string, BadgeVariant> = {
  active: 'success',
  published: 'success',
  confirmed: 'success',
  completed: 'success',
  inactive: 'default',
  draft: 'default',
  unpublished: 'default',
  pending: 'warning',
  awaiting_payment: 'warning',
  maintenance: 'warning',
  checked_out: 'info',
  checked_in: 'info',
  submitted: 'info',
  used: 'info',
  approved: 'success',
  cancelled: 'danger',
  expired: 'danger',
  rejected: 'danger',
  revoked: 'danger',
  terminated: 'danger',
  suspended: 'danger',
  banned: 'danger',
};

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  const variant = STATUS_VARIANT_MAP[status] ?? 'default';
  return (
    <Badge variant={variant} className={className}>
      {status.replace(/_/g, ' ')}
    </Badge>
  );
}
